import React, { useState } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { useHistory } from "react-router-dom";
import { Button, Form, FormGroup, Label, Input, Row, Col, Alert } from "reactstrap";
import Loading from "../components/Loading";
import LocationPicker from "../components/LocationPicker";
import { createOrganisation } from "../utils/apiService";
import { hasLocation } from "../utils/maps";
import { BUSINESS_TYPES, MONETARY_CURRENCIES } from "../utils/orgConstants";

const CreateOrganisationComponent = () => {
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [form, setForm] = useState({
    orgName: "",
    orgIdentifier: "",
    businessType: 1,
    monetaryCurrency: 840,
    latitude: 0,
    longitude: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [locationErrors, setLocationErrors] = useState({});

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === 'businessType' || name === 'monetaryCurrency' ? parseInt(value, 10) : value }));
  };

  const onLocationChange = ({ latitude, longitude }) => {
    setForm((prev) => ({ ...prev, latitude, longitude }));
    setLocationErrors({});
  };

  const validate = () => {
    const errors = {};

    if (!form.orgName || form.orgName.trim() === '') {
      errors.orgName = 'Organisation name is required';
    }

    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      errors.latitude = 'Latitude must be between -90 and 90';
    }

    if (isNaN(lng) || lng < -180 || lng > 180) {
      errors.longitude = 'Longitude must be between -180 and 180';
    }

    // Offers are found by distance from the business, so it needs a real place, not 0,0.
    if (!errors.latitude && !errors.longitude && !hasLocation(form.latitude, form.longitude)) {
      errors.location = 'Set where the business is: search the address or use your current location.';
    }

    if (!form.businessType) {
      errors.businessType = 'Business type is required';
    }

    return errors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLocationErrors({});

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      if (errors.latitude || errors.longitude) {
        setLocationErrors({ latitude: errors.latitude, longitude: errors.longitude });
      }
      setError(errors.orgName || errors.businessType || errors.location || 'Please fix the validation errors');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);

      // Build payload conforming to DC_Create_Org
      const payload = {
        orgName: form.orgName || null,
        businessType: form.businessType,
        monetaryCurrency: form.monetaryCurrency,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        // The API fills in the application and the creator; it refuses anything but an empty GUID.
        applicationGUID: "",
        orgIdentifier: form.orgIdentifier || "",
        isClaimed: 3,
        subscriptionDefinitionId: 0
      };

      const result = await createOrganisation(payload, token);
      const newId = result?.newRecordID > 0 ? result.newRecordID : (result?.id || result?.organisationId || result?.data?.id);
      if (newId) {
        history.push(`/organisations/${newId}`);
      } else {
        history.push("/organisations");
      }
    } catch (err) {
      setError(err?.message || "Failed to create organisation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ paddingBottom: "3rem" }}>
      <a
        href="#/"
        className="page-back"
        onClick={(e) => { e.preventDefault(); history.push('/organisations'); }}
      >
        ← Organisations
      </a>
      <div className="page-header">
        <div>
          <h2 className="page-title">Create organisation</h2>
          <p className="page-subtitle">Add your business and place it on the map, so offers can be found nearby</p>
        </div>
      </div>
      <div className="panel">
        <div className="panel-body">
          {error && (
            <Alert color="danger">{error}</Alert>
          )}
          <Form onSubmit={onSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="orgName">
                    Organisation Name <span className="text-danger">*</span>
                  </Label>
                  <Input id="orgName" name="orgName" value={form.orgName} onChange={onChange} required />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="orgIdentifier">Organisation Identifier</Label>
                  <Input id="orgIdentifier" name="orgIdentifier" value={form.orgIdentifier} onChange={onChange} />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="businessType">
                    Business Type <span className="text-danger">*</span>
                  </Label>
                  <Input id="businessType" name="businessType" type="select" value={form.businessType} onChange={onChange} required>
                    {BUSINESS_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="monetaryCurrency">Currency</Label>
                  <Input id="monetaryCurrency" name="monetaryCurrency" type="select" value={form.monetaryCurrency} onChange={onChange}>
                    {MONETARY_CURRENCIES.map(curr => (
                      <option key={curr.id} value={curr.id}>{curr.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <LocationPicker
              latitude={form.latitude}
              longitude={form.longitude}
              onChange={onLocationChange}
              required={true}
              errors={locationErrors}
            />
            <div className="d-flex justify-content-end mt-4 pt-3 border-top">
              <Button color="light" className="mr-2" type="button" onClick={() => history.push('/organisations')} disabled={submitting}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Organisation'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default withAuthenticationRequired(CreateOrganisationComponent, {
  onRedirecting: () => <Loading />,
});
